import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { FaUser, FaCamera } from "react-icons/fa";
import { useAuthStore } from "../../stores/authStore";
import {
  useGetProfile,
  useUpdateProfile,
  useChangePassword,
  useDeactivateAccount,
  useLogout,
} from "@/api/AuthApi";
import { handleApiErrors } from "@/utils/errorMapper";
import SmallLoader from "@/components/SmallLoader";

const profileSchema = z.object({
  first_name: z.string().min(1, "Le prénom est requis"),
  last_name: z.string().min(1, "Le nom est requis"),
  phone_number: z
    .string()
    .regex(
      /^\+\d{7,15}$/,
      "Format invalide. Utilisez +[indicatif][numéro], ex: +237123456789",
    ),
  newsletter_subscription: z.boolean(),
});

const changePasswordSchema = z
  .object({
    old_password: z.string().min(1, "Le mot de passe actuel est requis"),
    new_password: z
      .string()
      .min(8, "Le nouveau mot de passe doit comporter au moins 8 caractères"),
    confirm_password: z
      .string()
      .min(8, "Le mot de passe doit comporter au moins 8 caractères"),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: "Les mots de passe ne correspondent pas",
    path: ["confirm_password"],
  });

type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;

type ProfileFormData = z.infer<typeof profileSchema>;

const UserInfo: React.FC = () => {
  const { user } = useAuthStore();
  const { getProfile, loading: getProfileLoading } = useGetProfile();
  const { updateProfile, loading: updateLoading } = useUpdateProfile();
  const { logOutUser } = useLogout();

  const [isEditing, setIsEditing] = useState(false);
  const [profilePictureFile, setProfilePictureFile] = useState<File | null>(
    null,
  );
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
  });

  const { changePassword, loading: changePasswordLoading } =
    useChangePassword();
  const [showChangePassword, setShowChangePassword] = useState(false);

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    reset: resetPasswordForm,
    setError: setPasswordError,
    formState: { errors: passwordErrors },
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
  });

  const onChangePasswordSubmit = async (data: ChangePasswordFormData) => {
    const result = await changePassword(data);
    if (result.success) {
      resetPasswordForm();
      setShowChangePassword(false);
    } else if (result.error) {
      handleApiErrors(
        result.error,
        setPasswordError,
        "Impossible de modifier le mot de passe",
      );
    }
  };

  const { deactivateAccount, loading: deactivateLoading } =
    useDeactivateAccount();
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);

  const handleDeactivate = async () => {
    await deactivateAccount();
    // On success, useDeactivateAccount already clears tokens and navigates away.
    // No further action needed here even if the user is still mounted briefly.
  };

  useEffect(() => {
    if (!user) getProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) {
      reset({
        first_name: user.first_name,
        last_name: user.last_name,
        phone_number: user.phone_number || "",
        newsletter_subscription: user.newsletter_subscription || false,
      });
    }
  }, [user, reset]);

  const handlePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfilePictureFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setProfilePictureFile(null);
    setPreviewUrl(null);
    if (user) {
      reset({
        first_name: user.first_name,
        last_name: user.last_name,
        phone_number: user.phone_number || "",
        newsletter_subscription: user.newsletter_subscription || false,
      });
    }
  };

  const onSubmit = async (data: ProfileFormData) => {
    const result = await updateProfile(data, profilePictureFile);
    if (result.success) {
      setIsEditing(false);
      setProfilePictureFile(null);
      setPreviewUrl(null);
    } else if (result.error) {
      handleApiErrors(
        result.error,
        setError,
        "Impossible de mettre à jour le profil",
      );
    }
  };

  if (getProfileLoading && !user) {
    return (
      <div className="p-4 flex justify-center">
        <SmallLoader modal={true} />
      </div>
    );
  }

  if (!user) {
    return <p className="p-4">Veuillez vous connecter.</p>;
  }

  return (
    <div className="p-6 max-w-xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-6 relative">
        {updateLoading && <SmallLoader modal={true} />}

        <div className="flex flex-col items-center mb-6">
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
              {previewUrl || user.profile_picture ? (
                <img
                  src={previewUrl ?? user.profile_picture ?? undefined}
                  alt="Photo de profil"
                  className="w-full h-full object-cover"
                />
              ) : (
                <FaUser className="text-gray-400 text-4xl" />
              )}
            </div>
            {isEditing && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="absolute bottom-0 right-0 bg-blue-500 text-white p-2 rounded-full hover:bg-blue-600"
              >
                <FaCamera className="text-sm" />
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handlePictureChange}
            />
          </div>
          <h2 className="text-xl font-bold mt-3">
            {user.first_name} {user.last_name}
          </h2>
          <p className="text-sm text-gray-500">@{user.username}</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Prénom
              </label>
              <input
                type="text"
                disabled={!isEditing}
                {...register("first_name")}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100"
              />
              {errors.first_name && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.first_name.message}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Nom
              </label>
              <input
                type="text"
                disabled={!isEditing}
                {...register("last_name")}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100"
              />
              {errors.last_name && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.last_name.message}
                </p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Email
            </label>
            <input
              type="email"
              disabled
              value={user.email}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
            />
            <p className="text-xs text-gray-400 mt-1">
              L'email ne peut pas être modifié ici.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Téléphone
            </label>
            <input
              type="tel"
              disabled={!isEditing}
              {...register("phone_number")}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100"
            />
            {errors.phone_number && (
              <p className="text-red-500 text-xs mt-1">
                {errors.phone_number.message}
              </p>
            )}
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              disabled={!isEditing}
              {...register("newsletter_subscription")}
              className="h-4 w-4 text-blue-500 border-gray-300 rounded"
            />
            <label className="ml-2 text-sm text-gray-700">
              S'abonner à la newsletter
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            {isEditing ? (
              <>
                <button
                  type="submit"
                  className="flex-1 bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600"
                >
                  Enregistrer
                </button>
                <button
                  type="button"
                  onClick={cancelEditing}
                  className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300"
                >
                  Annuler
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="flex-1 bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600"
              >
                Modifier le profil
              </button>
            )}
          </div>
        </form>

        <div className="mt-6 pt-6 border-t border-gray-200">
          {showChangePassword ? (
            <div className="relative">
              {changePasswordLoading && <SmallLoader modal={true} />}
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Changer le mot de passe
              </h3>
              <form
                onSubmit={handlePasswordSubmit(onChangePasswordSubmit)}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Mot de passe actuel
                  </label>
                  <input
                    type="password"
                    {...registerPassword("old_password")}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  {passwordErrors.old_password && (
                    <p className="text-red-500 text-xs mt-1">
                      {passwordErrors.old_password.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Nouveau mot de passe
                  </label>
                  <input
                    type="password"
                    {...registerPassword("new_password")}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  {passwordErrors.new_password && (
                    <p className="text-red-500 text-xs mt-1">
                      {passwordErrors.new_password.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Confirmer le nouveau mot de passe
                  </label>
                  <input
                    type="password"
                    {...registerPassword("confirm_password")}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  {passwordErrors.confirm_password && (
                    <p className="text-red-500 text-xs mt-1">
                      {passwordErrors.confirm_password.message}
                    </p>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    type="submit"
                    className="flex-1 bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600"
                  >
                    Confirmer
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowChangePassword(false);
                      resetPasswordForm();
                    }}
                    className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowChangePassword(true)}
              className="text-blue-500 text-sm hover:text-blue-700"
            >
              Changer le mot de passe
            </button>
          )}
        </div>

        <button
          onClick={logOutUser}
          className="w-full mt-4 py-2 px-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Se déconnecter
        </button>

        <div className="mt-4 pt-4 border-t border-gray-200">
          {showDeactivateConfirm ? (
            <div className="relative bg-red-50 border border-red-200 rounded-lg p-4">
              {deactivateLoading && <SmallLoader modal={true} />}
              <p className="text-sm text-red-700 font-medium">
                Êtes-vous sûr de vouloir désactiver votre compte ?
              </p>
              <p className="text-xs text-red-600 mt-1">
                Cette action est irréversible depuis cette interface.
              </p>
              <div className="flex gap-3 mt-3">
                <button
                  type="button"
                  onClick={handleDeactivate}
                  className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700"
                >
                  Oui, désactiver mon compte
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeactivateConfirm(false)}
                  className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300"
                >
                  Annuler
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowDeactivateConfirm(true)}
              className="text-red-500 text-sm hover:text-red-700"
            >
              Désactiver mon compte
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserInfo;
